function normalizeValue(value) {
  return String(value || "").trim().toUpperCase();
}

function normalizeFallbackPaymentLabel(status) {
  switch (normalizeValue(status)) {
    case "PAID":
      return "Paid";
    case "FAILED":
      return "Failed";
    case "REFUNDED":
      return "Refunded";
    case "PENDING":
    case "UNPAID":
    default:
      return "Pending";
  }
}

function resolveHostedCheckoutLabel(checkoutPayment) {
  if (checkoutPayment?.label) return checkoutPayment.label;

  switch (normalizeValue(checkoutPayment?.state)) {
    case "PAID":
      return "Paid";
    case "EXPIRED":
      return "Payment Expired";
    case "CANCELLED":
      return "Cancelled";
    case "AWAITING_PAYMENT":
      return "Awaiting Payment";
    case "FAILED":
      return "Payment Failed";
    default:
      return normalizeFallbackPaymentLabel(checkoutPayment?.latestPaymentStatus);
  }
}

export function normalizeBusinessCheckoutPayment(checkoutPayment) {
  if (!checkoutPayment || typeof checkoutPayment !== "object") return null;

  return {
    ...checkoutPayment,
    state: normalizeValue(checkoutPayment.state),
    label: resolveHostedCheckoutLabel(checkoutPayment),
    message: checkoutPayment.message || null,
    canSettle: Boolean(checkoutPayment.canSettle ?? checkoutPayment.can_settle),
  };
}

export function isBusinessHostedCheckoutOrder(order) {
  return normalizeValue(order?.paymentMethod || order?.payment_method) === "PAYMONGO_CHECKOUT";
}

export function getBusinessPaymentPresentation(order) {
  const checkoutPayment = normalizeBusinessCheckoutPayment(order?.checkoutPayment || order?.checkout_payment);
  if (checkoutPayment) {
    return {
      label: checkoutPayment.label,
      helperText: checkoutPayment.message,
      systemManaged: isBusinessHostedCheckoutOrder(order),
      checkoutPayment,
    };
  }

  return {
    label: normalizeFallbackPaymentLabel(order?.paymentStatus || order?.payment_status),
    helperText: null,
    systemManaged: isBusinessHostedCheckoutOrder(order),
    checkoutPayment: null,
  };
}

export function isBusinessCodOrder(order) {
  return normalizeValue(order?.paymentMethod || order?.payment_method) === "COD";
}

// COD payment follows the Rider's cash collection and the Business's
// acceptance of the Rider's remittance; the server refuses manual changes.
export function canBusinessEditPaymentStatus(order) {
  return !isBusinessHostedCheckoutOrder(order) && !isBusinessCodOrder(order);
}

export function getBusinessPaymentSystemNote(order) {
  if (isBusinessCodOrder(order)) {
    return "Updates automatically when the Rider collects the cash and you accept the Rider's remittance.";
  }
  return "System-managed by PayMongo hosted checkout.";
}

export function canBusinessEditOrderStatus(order) {
  if (!isBusinessHostedCheckoutOrder(order)) return true;

  const checkoutPayment = normalizeBusinessCheckoutPayment(order?.checkoutPayment || order?.checkout_payment);
  if (checkoutPayment) {
    return checkoutPayment.state === "PAID";
  }

  return normalizeValue(order?.paymentStatus || order?.payment_status) === "PAID";
}

export function getBusinessOrderLifecycleNotice(order) {
  if (!isBusinessHostedCheckoutOrder(order)) return null;

  const checkoutPayment = normalizeBusinessCheckoutPayment(order?.checkoutPayment || order?.checkout_payment);
  if (checkoutPayment?.state === "PAID") return null;
  if (checkoutPayment?.message) return checkoutPayment.message;

  return "Hosted checkout payment is still pending. Fulfillment stays locked until shopper payment is confirmed.";
}